import React from 'react'
import {Layer, Stage} from "react-konva";
import Konva from "konva";
import ImageItem from "./shapes/image-item";

const stageWidth = window.innerWidth * 3 / 4;
const stageHeight = window.innerHeight * 3 / 4;
const GUIDELINE_OFFSET = 5;

function generateShapes() {
    // first generate random rectangles
    return [...Array(5)]
        .map((_, index) => (
            {
                id: 'image-' + index,
                x: Math.random() * stageWidth,
                y: Math.random() * stageHeight,
                width: 100 + (index * 50),
                height: 100 + (index * 50),
                imageUrl: 'assets/images/background/space-splash001.png',
                draggable: true,
                name: 'object',
            }));
}

// function from https://stackoverflow.com/a/15832662/512042
function downloadURI(uri, name) {
    let link = document.createElement('a');
    link.download = name;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const INITIAL_SHAPES = generateShapes();

const SnapImageTransformer = () => {
    const stageRef = React.useRef(null);
    const layerRef = React.useRef(null);

    // const [rectangles, setRectangles] = React.useState(INITIAL_SHAPES);
    const [imageDetailList, setImageDetailList] = React.useState(INITIAL_SHAPES);
    const [selectedId, selectShape] = React.useState(null);

    const checkDeselect = (event) => {
        // deselect when clicked on empty area
        const clickedOnEmpty = event.target === event.target.getStage();
        if (clickedOnEmpty) {
            selectShape(null);
            const layer = layerRef.current;
            // clear all previous lines on the screen
            layer.find('.guid-line').forEach((l) => l.destroy());
        }
    };

    const handleExport = () => {
        const uri = stageRef.current.toDataURL();
        // we also can save uri as file
        // but in the demo on Konva website it will not work
        // because of iframe restrictions
        // but feel free to use it in your apps:
        downloadURI(uri, 'stage.png');
    };

// were can we snap our objects?
    const getLineGuideStops = (skipShape) => {
        // we can snap to stage borders and the center of the stage
        let vertical = [0, stageWidth / 2, stageWidth];
        let horizontal = [0, stageHeight / 2, stageHeight];

        const stage = stageRef.current;

        // and we snap over edges and center of each object on the canvas
        stage.find('.object').forEach((guideItem) => {
            if (guideItem === skipShape) {
                return;
            }
            let box = guideItem.getClientRect();
            // and we can snap to all edges of shapes
            // @ts-ignore
            vertical.push([box.x, box.x + box.width, box.x + box.width / 2]);
            // @ts-ignore
            horizontal.push([box.y, box.y + box.height, box.y + box.height / 2]);
        });
        return {
            vertical: vertical.flat(),
            horizontal: horizontal.flat(),
        };
    }

// what points of the object will trigger to snapping?
// it can be just center of the object
// but we will enable all edges and center
    const getObjectSnappingEdges = (node) => {
        let box = node.getClientRect();
        let absPos = node.absolutePosition();

        return {
            vertical: [
                {
                    guide: Math.round(box.x),
                    offset: Math.round(absPos.x - box.x),
                    snap: 'start',
                },
                {
                    guide: Math.round(box.x + box.width / 2),
                    offset: Math.round(absPos.x - box.x - box.width / 2),
                    snap: 'center',
                },
                {
                    guide: Math.round(box.x + box.width),
                    offset: Math.round(absPos.x - box.x - box.width),
                    snap: 'end',
                },
            ],
            horizontal: [
                {
                    guide: Math.round(box.y),
                    offset: Math.round(absPos.y - box.y),
                    snap: 'start',
                },
                {
                    guide: Math.round(box.y + box.height / 2),
                    offset: Math.round(absPos.y - box.y - box.height / 2),
                    snap: 'center',
                },
                {
                    guide: Math.round(box.y + box.height),
                    offset: Math.round(absPos.y - box.y - box.height),
                    snap: 'end',
                },
            ],
        };
    }

    // find all snapping possibilities
    const getGuides = (lineGuideStops, itemBounds) => {
        let resultV = [];
        let resultH = [];

        lineGuideStops.vertical.forEach((lineGuide) => {
            itemBounds.vertical.forEach((itemBound) => {
                let diff = Math.abs(lineGuide - itemBound.guide);
                // if the distance between guild line and object snap point is close we can consider this for snapping
                if (diff < GUIDELINE_OFFSET) {
                    resultV.push({
                        lineGuide: lineGuide,
                        diff: diff,
                        snap: itemBound.snap,
                        offset: itemBound.offset,
                    });
                }
            });
        });

        lineGuideStops.horizontal.forEach((lineGuide) => {
            itemBounds.horizontal.forEach((itemBound) => {
                let diff = Math.abs(lineGuide - itemBound.guide);
                if (diff < GUIDELINE_OFFSET) {
                    resultH.push({
                        lineGuide: lineGuide,
                        diff: diff,
                        snap: itemBound.snap,
                        offset: itemBound.offset,
                    });
                }
            });
        });

        let guides = [];

        // find closest snap
        let minV = resultV.sort((a, b) => a.diff - b.diff)[0];
        let minH = resultH.sort((a, b) => a.diff - b.diff)[0];
        if (minV) {
            guides.push({
                lineGuide: minV.lineGuide,
                offset: minV.offset,
                orientation: 'V',
                snap: minV.snap,
            });
        }
        if (minH) {
            guides.push({
                lineGuide: minH.lineGuide,
                offset: minH.offset,
                orientation: 'H',
                snap: minH.snap,
            });
        }
        return guides;
    }

    const drawGuides = (guides) => {
        const layer = layerRef.current;

        guides.forEach((lg) => {
            if (lg.orientation === 'H') {
                let line = new Konva.Line({
                    points: [-6000, 0, 6000, 0],
                    stroke: 'rgb(0, 161, 255)',
                    strokeWidth: 1,
                    name: 'guid-line',
                    dash: [4, 6],
                });

                layer.add(line);
                line.absolutePosition({
                    x: 0,
                    y: lg.lineGuide,
                });
            } else if (lg.orientation === 'V') {
                let line = new Konva.Line({
                    points: [0, -6000, 0, 6000],
                    stroke: 'rgb(0, 161, 255)',
                    strokeWidth: 1,
                    name: 'guid-line',
                    dash: [4, 6],
                });
                layer.add(line);
                line.absolutePosition({
                    x: lg.lineGuide,
                    y: 0,
                });
            }
        });
    }

    const handleLayerDragMove = (event) => {
        // const layer = event.target.getLayer();
        const layer = layerRef.current;
        // clear all previous lines on the screen
        layer.find('.guid-line').forEach((l) => l.destroy());

        // find possible snapping lines
        let lineGuideStops = getLineGuideStops(event.target);
        // find snapping points of current object
        let itemBounds = getObjectSnappingEdges(event.target);

        // now find where can we snap current object
        let guides = getGuides(lineGuideStops, itemBounds);

        // do nothing of no snapping
        if (!guides.length) {
            return;
        }

        drawGuides(guides);

        let absPos = event.target.absolutePosition();
        // now force object position
        guides.forEach((lg) => {
            switch (lg.snap) {
                case 'start': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
                case 'center': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
                case 'end': {
                    switch (lg.orientation) {
                        case 'V': {
                            absPos.x = lg.lineGuide + lg.offset;
                            break;
                        }
                        case 'H': {
                            absPos.y = lg.lineGuide + lg.offset;
                            break;
                        }
                    }
                    break;
                }
            }
        });

        event.target.absolutePosition(absPos);
    }

    return (
        <>
            <button onClick={handleExport}>Click here to log stage data URL</button>

            <Stage width={window.innerWidth * 3 / 4} height={window.innerHeight * 3 / 4}
                   onMouseDown={checkDeselect}
                   onTouchStart={checkDeselect}
                   ref={stageRef}
                   draggable
                   className="">
                <Layer
                    ref={layerRef}
                    onDragMove={handleLayerDragMove}>
                    {imageDetailList.map((imageDetail, i) => {
                        return (
                            <ImageItem
                                key={i}
                                shapeProps={imageDetail}
                                isSelected={imageDetail.id === selectedId}
                                onSelect={() => {
                                    selectShape(imageDetail.id);
                                }}
                                onChange={(newAttrs) => {
                                    const rects = imageDetailList.slice();
                                    rects[i] = newAttrs;
                                    setImageDetailList(rects);
                                }}
                            />
                        )
                    })}
                </Layer>
            </Stage>
        </>
    )
}

export default SnapImageTransformer
